import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    GetAttributesFilter,
    GetConditionFilter,
    GetValuesByOptionAndField,
    GetValuesByOptionFieldAndUserId,
    GetValuesToFilterLogByField,
} from '../../../reduxToolkit/Search/SearchApi';
import { fieldModel } from '../../../enums/fieldModelEnum';
import { toast } from 'react-toastify';
import {
    NULL_OPERATIONS,
    COMPARISON_OPERATIONS,
    createEmptyRule,
    createEmptyGroup,
    createStandaloneRule,
    computeInputFlags,
    validateGroupRules,
    validateItems,
    ITEM_TYPE_MODEL,
    createFieldChangeUpdate,
    createConditionChangeUpdate,
    createValueUpdate,
    createAssignedListUpdate
} from '../SearchFor/Model/Model';
import {getDateFormat, getEndOfDay} from "../../../Apis/functions";
import {DATA_TYPE_MODEL, getFieldNameByValue, getFieldValueTrans, SEARCH_OPERATION_MODEL} from "../Model/Model";
import fieldId from "../../../hooks/fieldId";
import conditionId from "../../../hooks/conditionId";
import { fieldNameEnum } from "../../../enums/fieldNameEnum";
import RuleRow from "../SearchFor/SubComponents/RuleRow";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Logic hook for the advanced search builder modal.
 * Manages the items state (rules and groups), fetches filter attributes and
 * condition options from the API, validates inputs, and builds the payload
 * sent to onFiltered on search.
 *
 * @param {boolean} open - Whether the modal is open; triggers attribute fetch.
 * @param {Function} onFiltered - Callback receiving the built payload array on search or clear.
 * @param {number|string} moduleId - Module ID used to fetch available filter attributes.
 * @param {string} option - API option string passed to value-fetching endpoints.
 * @param {Array} finalArray - Current active filters from the parent; used to detect dirty state.
 * @param {string} [modelType] - If set, uses GetValuesToFilterLogByField instead of standard API.
 * @param {Function} setSearch - Parent setter to flag that a search is active.
 * @param {boolean} cleanAdvance - External signal to clear all filters.
 * @param {Function} setCleanAdvance - Parent setter for cleanAdvance flag.
 * @param {boolean} cleanRules - External signal to clear all rules (same effect as cleanAdvance).
 * @param {Function} setFinalArray - Parent setter to reset the active filters array.
 * @param {boolean} searchElement - When true, resets filters on open and notifies user.
 * @param {number|string|null} [userId=null] - Overrides contractorId from sessionStorage.
 * @param {Array|null} [noShowFilters=null] - Attribute IDs to exclude from the filter list.
 * @param {Function|null} [callGetValuesApi=null] - Custom value-fetching callback; skips default API calls when set.
 * @returns {Object} Handlers and state consumed by SearchFor and its sub-components.
 */
const SearchForApp = ({
    open,
    onFiltered,
    moduleId,
    option,
    finalArray,
    modelType,
    setSearch,
    cleanAdvance,
    setCleanAdvance,
    cleanRules,
    setFinalArray,
    searchElement,
    userId = null,
    noShowFilters = null,
    callGetValuesApi = null,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [items, setItems] = useState([createStandaloneRule()]);
    const [attributesFilter, setAttributesFilter] = useState([]);
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId] = useState(sessionStorage.getItem('providerId'));
    const [summaryVisible, setSummaryVisible] = useState(true);

    /**
     * Syncs contractorId with the userId prop.
     * When userId is provided it takes priority over the sessionStorage value.
     */
    useEffect(() => {
        if (userId) setContractorId(userId);
        else setContractorId(sessionStorage.getItem('contractorId'));
    }, [userId]);

    /**
     * Clears all filters when the parent signals a reset via cleanAdvance or cleanRules.
     */
    useEffect(() => {
        if (cleanAdvance || cleanRules) handleClear();
    }, [cleanAdvance]);

    /**
     * Resets active filters when searchElement is true and finalArray has entries.
     * Notifies the user that previously applied filters were cleared.
     */
    useEffect(() => {
        if (finalArray?.length > 0 && searchElement) {
            setFinalArray([]);
            toast.info(t('filters_reset_message'));
        }
    }, [finalArray, searchElement]);

    /**
     * Notifies the user on modal open when searchElement is active,
     * indicating that filters will be reset for this context.
     */
    useEffect(() => {
        if (open && searchElement) toast.info(t('filters_reset_message'));
    }, [open, searchElement]);

    /**
     * Fetches the available filter attributes for the current module when the modal opens.
     * Applies noShowFilters exclusion list if provided.
     */
    useEffect(() => {
        if (!open) return;
        dispatch(GetAttributesFilter(moduleId)).then(({ payload: { data: { data } } }) => {
            if (noShowFilters === null) setAttributesFilter(data);
            else setAttributesFilter(data.filter(e => !noShowFilters.includes(e.id)));
        });
    }, [open]);

    /**
     * Merges partial updates into a rule identified by itemId + ruleId.
     * Works for standalone rules (type RULE) and rules nested inside groups (type GROUP).
     *
     * @param {string} itemId - ID of the top-level item (rule or group) to target.
     * @param {string} ruleId - ID of the rule to update; ignored for standalone rules.
     * @param {Object} updates - Partial rule fields to merge.
     */
    const updateRuleData = (itemId, ruleId, updates) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId) return item;
            if (item.type === ITEM_TYPE_MODEL.RULE) return { ...item, ...updates };
            return {
                ...item,
                rules: item.rules.map(rule => rule.id !== ruleId ? rule : { ...rule, ...updates }),
            };
        }));
    };

    /**
     * Fetches available option values for a field based on the current context.
     * Returns [] immediately for DATE fields. Routes to:
     *   - callGetValuesApi (custom callback) when provided,
     *   - GetValuesToFilterLogByField when modelType is set,
     *   - GetValuesByOptionFieldAndUserId when a contractorId or supplierId is present,
     *   - GetValuesByOptionAndField otherwise.
     *
     * @param {string} typeAttribute - Data type of the field (see DATA_TYPE_MODEL).
     * @param {string} fieldName - Backend filter key for the field.
     * @returns {Promise<Array>} Resolved list of option values, or [] on failure.
     */
    const fetchValues = async (typeAttribute, fieldName) => {
        if (typeAttribute === DATA_TYPE_MODEL.DATE) return [];

        let result;
        if (callGetValuesApi !== null) {
            result = await callGetValuesApi({ option, fieldName, userId });
        } else if (modelType !== undefined && modelType !== '') {
            result = await dispatch(GetValuesToFilterLogByField({ model: modelType, field: fieldName }));
        } else if (contractorId === null && supplierId === null) {
            result = await dispatch(GetValuesByOptionAndField({ option, field: fieldName }));
        } else {
            result = await dispatch(GetValuesByOptionFieldAndUserId({
                option,
                field: fieldName,
                userId: contractorId !== null ? contractorId : supplierId,
            }));
        }
        return result?.payload?.data?.data || [];
    };

    /**
     * Handles field selection in a rule row. Resets the rule to a clean state,
     * then fetches conditions and available values for the selected attribute in parallel.
     *
     * @param {string} itemId - ID of the parent item (rule or group).
     * @param {string} ruleId - ID of the rule being updated.
     * @param {number|string} fieldId - ID of the selected attribute from attributesFilter.
     */
    const handleFieldChange = async (itemId, ruleId, fieldId) => {
        const attr = attributesFilter.find(a => a.id === fieldId);
        if (!attr) return;

        updateRuleData(itemId, ruleId, createFieldChangeUpdate(
            fieldId,
            attr.field,
            attr.fieldType,
            fieldModel[fieldId] || '',
        ));

        const condResult = await dispatch(GetConditionFilter(attr.fieldType));
        const conditions = condResult?.payload?.data?.data || [];
        updateRuleData(itemId, ruleId, { conditions, values: [] });
    };

    /**
     * Handles condition selection in a rule row. Computes input visibility flags
     * based on the field type and operation, then updates the rule.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {number|string} conditionId - ID of the selected condition.
     * @param {string} operationKey - Operation key of the selected condition.
     * @param {Object} rule - Current rule state, used to compute input flags.
     */
    const handleConditionChange = async (itemId, ruleId, conditionId, operationKey, rule) => {
        const skipFetch = [...NULL_OPERATIONS, ...COMPARISON_OPERATIONS].includes(operationKey);
        let values = rule.values;
        if (!skipFetch && values.length === 0) {
            values = await fetchValues(rule.typeAttribute, rule.fieldName);
        }
        const flags = computeInputFlags(rule?.typeAttribute?.toUpperCase?.(), operationKey, values, rule.fieldName, t);
        updateRuleData(itemId, ruleId, { ...createConditionChangeUpdate(conditionId, operationKey, flags), values });
    };

    /**
     * Handles text or number input change in a rule.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {string|number} value - New input value.
     */
    const handleValueChange = (itemId, ruleId, value) => {
        updateRuleData(itemId, ruleId, createValueUpdate(value));
    };

    /**
     * Handles single date picker change in a rule.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {Date|null} date - Selected date value.
     */
    const handleDateChange = (itemId, ruleId, date) => {
        updateRuleData(itemId, ruleId, { dateAttribute: date });
    };

    /**
     * Handles the "from" date picker change in a date range rule.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {Date|null} date - Selected start date.
     */
    const handleFromDateChange = (itemId, ruleId, date) => {
        updateRuleData(itemId, ruleId, { fromValue: date });
    };

    /**
     * Handles the "to" date picker change in a date range rule.
     * Normalizes the date to 23:59:59.999 to make the end of day inclusive.
     * Ignores null values to avoid clearing a previously valid date.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {Date|null} date - Selected end date.
     */
    const handleToDateChange = (itemId, ruleId, date) => {
        if (!date) return;
        updateRuleData(itemId, ruleId, { toValue: getEndOfDay(date) });
    };

    /**
     * Returns a new rule object with the given field updated to val.
     * For inputTwoNumbers rules, also recomputes isValidTo (toValue >= fromValue).
     * isValidTo is true when either bound is empty, since there is nothing to compare yet.
     *
     * @param {Object} rule - Current rule state.
     * @param {'fromValue'|'toValue'} field - Which bound to update.
     * @param {string|number} val - New value for the updated bound.
     * @returns {Object} Updated rule with isValidTo recomputed when applicable.
     */
    const applyRangeUpdate = (rule, field, val) => {
        const next = { ...rule, [field]: val };
        if (!rule.inputTwoNumbers) return next;
        return {
            ...next,
            isValidTo: (!next.fromValue || !next.toValue)
                ? true
                : parseFloat(next.toValue) >= parseFloat(next.fromValue),
        };
    };

    /**
     * Handles a "from" or "to" number input change in a numeric range rule.
     * Delegates to applyRangeUpdate so that isValidTo is recomputed atomically
     * with the value change in the same setState call.
     *
     * @param {string} itemId - ID of the parent item (standalone rule or group).
     * @param {string} ruleId - ID of the rule inside a group; ignored for standalone rules.
     * @param {'fromValue'|'toValue'} field - Which bound of the range to update.
     * @param {string|number} val - New numeric value.
     */
    const handleRangeValueChange = (itemId, ruleId, field, val) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId) return item;
            if (item.type === ITEM_TYPE_MODEL.RULE) return applyRangeUpdate(item, field, val);
            return {
                ...item,
                rules: item.rules.map(rule =>
                    rule.id !== ruleId ? rule : applyRangeUpdate(rule, field, val)
                ),
            };
        }));
    };

    /**
     * Handles selection changes in a transferable list input.
     * Recomputes assignedList and availablesList from the merged pool of both lists.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {Array<number|string>} selectedIds - IDs of the currently selected items.
     * @param {Object} rule - Current rule state, used to access availablesList and assignedList.
     */
    const handleAssignMultiple = (itemId, ruleId, selectedIds, rule) => {
        const allOptions = [...rule.availablesList, ...rule.assignedList];
        updateRuleData(itemId, ruleId, createAssignedListUpdate(
            allOptions.filter(i => selectedIds.includes(i.id)),
            allOptions.filter(i => !selectedIds.includes(i.id)),
        ));
    };

    /**
     * Updates a single validity flag on a rule (e.g. isValidDate, isValidFrom, isValidTo).
     * Called by date pickers via their onError callback.
     *
     * @param {string} itemId - ID of the parent item.
     * @param {string} ruleId - ID of the rule being updated.
     * @param {string} field - Name of the validity flag to update.
     * @param {boolean} value - New validity value.
     */
    const handleValidityChange = (itemId, ruleId, field, value) => {
        updateRuleData(itemId, ruleId, { [field]: value });
    };

    const handleItemConnectorChange = (itemId, connector) => {
        setItems(prev => prev.map(item => item.id !== itemId ? item : { ...item, connector }));
    };

    /**
     * Toggles the AND/OR connector of a rule inside a group.
     * The connector determines how this rule joins the previous rule within the group.
     *
     * @param {string} groupId - ID of the group containing the rule.
     * @param {string} ruleId - ID of the rule whose connector changes.
     * @param {string} connector - New connector value (SEARCH_OPERATION_MODEL.AND | OR).
     */
    const handleRuleConnectorChange = (groupId, ruleId, connector) => {
        setItems(prev => prev.map(item => {
            if (item.id !== groupId || item.type !== ITEM_TYPE_MODEL.GROUP) return item;
            return {
                ...item,
                rules: item.rules.map(rule => rule.id !== ruleId ? rule : { ...rule, connector }),
            };
        }));
    };

    /**
     * Adds a new empty group to the items list.
     * If the current state is a single empty standalone rule, replaces it with the group.
     * Otherwise validates existing items first; does not add if any item is invalid.
     */
    const handleAddGroup = () => {
        setItems(prev => {
            const isDefaultEmpty = prev.length === 1
                && prev[0].type === ITEM_TYPE_MODEL.RULE
                && !prev[0].field;
            if (isDefaultEmpty) return [createEmptyGroup()];
            const { validated, allValid } = validateItems(prev);
            if (!allValid) return validated;
            return [...validated, createEmptyGroup()];
        });
    };

    /**
     * Removes a group/rule from the items list.
     * If removing leaves the list empty, resets to a single standalone rule.
     *
     * @param {string} id - ID of the group/rule to remove.
     */
    const handleDeleteItem = (id) => {
        setItems(prev => {
            const updated = prev.filter(item => item.id !== id);
            return updated.length === 0 ? [createStandaloneRule()] : updated;
        });
    };

    /**
     * Adds a new empty rule inside a group.
     * Validates existing rules in the group first; does not add if any is invalid.
     *
     * @param {string} groupId - ID of the group to add a rule to.
     */
    const handleAddRuleToGroup = (groupId) => {
        setItems(prev => {
            const target = prev.find(item => item.id === groupId);
            if (!target) return prev;
            const { validated: validatedRules, allValid } = validateGroupRules(target.rules);
            const updated = prev.map(item =>
                item.id !== groupId ? item : { ...item, rules: validatedRules }
            );
            if (!allValid) return updated;
            return updated.map(item =>
                item.id !== groupId ? item : { ...item, rules: [...item.rules, createEmptyRule()] }
            );
        });
    };

    /**
     * Removes a rule from inside a group.
     *
     * @param {string} groupId - ID of the group containing the rule.
     * @param {string} ruleId - ID of the rule to remove.
     */
    const handleDeleteRuleFromGroup = (groupId, ruleId) => {
        setItems(prev => prev.map(item => {
            if (item.id !== groupId || item.type !== ITEM_TYPE_MODEL.GROUP) return item;
            return { ...item, rules: item.rules.filter(r => r.id !== ruleId) };
        }));
    };

    /**
     * Adds a new standalone rule at the top level of the items list.
     * If the current state is a single empty group, replaces it with the rule.
     * Otherwise validates existing items first; does not add if any is invalid.
     */
    const handleAddTopLevelRule = () => {
        setItems(prev => {
            const isDefaultEmpty = prev.length === 1
                && prev[0].type === ITEM_TYPE_MODEL.GROUP
                && prev[0].rules.length === 1
                && !prev[0].rules[0].field;
            if (isDefaultEmpty) return [createStandaloneRule()];

            const { validated, allValid } = validateItems(prev);
            if (!allValid) return validated;
            return [...validated, createStandaloneRule()];
        });
    };


    /**
     * Converts a single validated rule into the backend payload shape.
     * Selects the value format based on the active input type:
     *   - transferable list → values array of ids or names depending on field type
     *   - single date       → values: [timestamp]
     *   - date range        → from/to as timestamps
     *   - number range      → from/to as floats
     *   - null operation    → only operation key, no value
     *   - default           → values: [valueAttribute]
     *
     * @param {Object} rule - Validated rule object from the search builder state.
     * @returns {Object} Rule payload with fieldType, filterKey, sort, table, operation, and value fields.
     */
    const buildRulePayload = (rule) => {
        const base = {
            fieldType: rule.typeAttribute,
            filterKey: rule.fieldName,
            sort: rule.sort,
            table: rule.modelName,
        };

        if (!rule.condition) return base;

        if (NULL_OPERATIONS.includes(rule.operationKey)) {
            return { ...base, operation: rule.operationKey };
        }

        if (rule.inputTransferableList) {
            const nameList = rule.assignedList.map(i => i.name);
            const idList = rule.assignedList.map(i => i.id);
            return {
                ...base,
                operation: rule.operationKey,
                values: (rule.typeAttribute === DATA_TYPE_MODEL.UUID || getFieldNameByValue(rule.fieldName))
                    ? idList : nameList,
            };
        }

        if (rule.inputDate) {
            return {
                ...base,
                operation: rule.operationKey,
                values: [rule.dateAttribute.getTime()],
            };
        }

        if (rule.inputTwoDates) {
            return {
                ...base,
                operation: rule.operationKey,
                from: rule.fromValue.getTime(),
                to: rule.toValue.getTime(),
            };
        }

        if (rule.inputTwoNumbers) {
            const isBigDecimal = rule.typeAttribute === DATA_TYPE_MODEL.BIGDECIMAL;
            return {
                ...base,
                operation: rule.operationKey,
                from: isBigDecimal ? parseFloat(rule.fromValue).toFixed(2) : parseFloat(rule.fromValue),
                to: isBigDecimal ? parseFloat(rule.toValue).toFixed(2) : parseFloat(rule.toValue),
            };
        }

        let value = rule.valueAttribute;
        if (rule.typeAttribute === DATA_TYPE_MODEL.UUID) {
            const found = (rule.values || []).find(v => v.id === value);
            if (found) value = found.id;
        } else if (rule.typeAttribute === DATA_TYPE_MODEL.BIGDECIMAL) {
            value = parseFloat(value).toFixed(2);
        }

        return { ...base, operation: rule.operationKey, values: [value] };
    };

    /**
     * Validates all items, builds the payload array, and calls onFiltered.
     * Standalone rules: { dataOption, ...ruleFields } — dataOption = item.connector (backward: how this item connects to the previous).
     * Groups: { rules: [{dataOption, ...ruleFields}, ...] } — first inner rule carries the group's outer connector; subsequent rules carry their own backward connectors.
     * CRITERIA_MODEL.set builds the expression tree using left-fold (no operator precedence).
     *
     * If all items have no field (empty form) and there are active filters, clears them.
     */
    const handleSearch = () => {
        const { validated: validatedItems, allValid } = validateItems(items);
        setItems(validatedItems);
        if (!allValid) return;

        const payload = [];

        validatedItems.forEach(item => {
            if (item.type === ITEM_TYPE_MODEL.RULE) {
                if (!item.field) return;
                payload.push({ dataOption: item.connector, ...buildRulePayload(item) });
            } else {
                const rules = item.rules
                    .filter(r => r.field)
                    .map((r, idx) => ({
                        dataOption: idx === 0 ? item.connector : r.connector,
                        ...buildRulePayload(r),
                    }));
                if (rules.length === 0) return;
                payload.push({ rules });
            }
        });

        if (payload.length === 0 && finalArray.length > 0) {
            setCleanAdvance(true);
            return;
        }

        onFiltered(payload);
        setSearch(true);
        setCleanAdvance(false);
    };

    /**
     * Resets the builder to a single empty standalone rule.
     * Only calls onFiltered([]) and updates search flags when there are active filters,
     * avoiding unnecessary re-fetches on an already clean state.
     */
    const handleClear = () => {
        setItems([createStandaloneRule()]);
        setSummaryVisible(true);
        if (finalArray?.length > 0) {
            onFiltered([]);
            setSearch(false);
            setCleanAdvance(true);
        }
    };


    /**
     * Converts a single rule object into a human-readable summary string.
     * Mirrors the display logic of RuleValueInput.renderDropdownOptions so that
     * labels in the preview always match what the user sees in the input controls.
     *
     * Returns null when the rule has no field selected yet.
     * Returns "[field sort]" when no condition is selected.
     * Returns "[field condition "..." sort]" when condition is set but value is empty.
     *
     * @param {Object} rule - Rule object from the items state.
     * @returns {string|null} Readable text for the rule, or null if the rule is blank.
     */
    const getRuleText = (rule) => {
        if (!rule.field) return null;
        const field = fieldId(rule.field);
        const condition = rule.condition ? conditionId(rule.condition) : '';

        if (!condition) return `${field} ...`;

        let value = '';
        if (rule.inputTransferableList) {
            value = rule.assignedList.map(i => i.name).join(', ');
        } else if (rule.inputDate && rule.dateAttribute) {
            value = `${getDateFormat(rule.dateAttribute, true)}`;
        } else if (rule.inputTwoDates) {
            if (rule.fromValue && rule.toValue) {
                value = `${getDateFormat(rule.fromValue, true)} - ${getDateFormat(rule.toValue, true)}`;
            }
        } else if (rule.inputTwoNumbers) {
            if (rule.fromValue != null && rule.toValue != null) {
                value = `${rule.fromValue} - ${rule.toValue}`;
            }
        } else if (!NULL_OPERATIONS.includes(rule.operationKey)) {
            if (rule.inputDropdown) {
                const safeValues = rule.values || [];
                if (rule.typeAttribute === DATA_TYPE_MODEL.BOOLEAN) {
                    const raw = rule.valueAttribute;
                    value = raw !== null && raw !== undefined && raw !== ''
                        ? t(String(raw).toLowerCase()).toUpperCase()
                        : '';
                } else if (rule.typeAttribute === DATA_TYPE_MODEL.UUID) {
                    value = safeValues.find(v => v.id === rule.valueAttribute)?.name || '';
                } else if (getFieldNameByValue(rule.fieldName)) {
                    const key = getFieldValueTrans(fieldNameEnum, rule.fieldName, rule.valueAttribute);
                    value = key ? t(key).toUpperCase() : String(rule.valueAttribute ?? '');
                } else {
                    value = rule.valueAttribute != null ? String(rule.valueAttribute) : '';
                }
            } else {
                value = rule.valueAttribute || '';
            }
        }

        if (!value && !NULL_OPERATIONS.includes(rule.operationKey)) {
            return [field, condition, '...'].filter(Boolean).join(' ');
        }

        const quotedValue = value ? `"${value}"` : '';
        return [field, condition, quotedValue].filter(Boolean).join(' ');
    };

    /**
     * Builds a full paragraph-style summary of all active filter rules.
     * Standalone rules are wrapped in brackets: [field condition "value" sort].
     * Groups are wrapped in parentheses containing their bracketed rules joined by connectors.
     * Items are joined by their AND/OR connector translated via i18next.
     *
     * @returns {string} Complete summary string, or empty string when items list is blank.
     */
    const buildRulesSummary = () => {
        const parts = [];
        items.forEach((item, idx) => {
            const connectorLabel = idx > 0 ? ` ${t(item.connector === SEARCH_OPERATION_MODEL.OR ? 'or' : 'and')} ` : '';
            if (item.type === ITEM_TYPE_MODEL.RULE) {
                const text = getRuleText(item);
                if (text) parts.push(`${connectorLabel}[${text}]`);
            } else {
                const groupParts = [];
                item.rules.forEach((rule, ri) => {
                    const text = getRuleText(rule);
                    if (text) {
                        const ruleConnector = ri > 0 ? ` ${t(rule.connector === SEARCH_OPERATION_MODEL.OR ? 'or' : 'and')} ` : '';
                        groupParts.push(`${ruleConnector}[${text}]`);
                    }
                });
                if (groupParts.length > 0) parts.push(`${connectorLabel}(${groupParts.join('')})`);
            }
        });
        return parts.join('');
    };

    const summary = buildRulesSummary();

    const renderStandaloneRule = (item) => (
        <>
            <RuleRow
                t={t}
                rule={item}
                attributesFilter={attributesFilter}
                showDelete={items?.length > 1}
                onDelete={() => handleDeleteItem(item.id)}
                onFieldChange={(fId) => handleFieldChange(item.id, null, fId)}
                onConditionChange={(cId, opKey) => handleConditionChange(item.id, null, cId, opKey, item)}
                onValueChange={(val) => handleValueChange(item.id, null, val)}
                onDateChange={(date) => handleDateChange(item.id, null, date)}
                onFromDateChange={(date) => handleFromDateChange(item.id, null, date)}
                onToDateChange={(date) => handleToDateChange(item.id, null, date)}
                onFromValueChange={(val) => handleRangeValueChange(item.id, null, 'fromValue', val)}
                onToValueChange={(val) => handleRangeValueChange(item.id, null, 'toValue', val)}
                onAssignMultiple={(selectedIds) => handleAssignMultiple(item.id, null, selectedIds, item)}
                onValidityChange={(field, val) => handleValidityChange(item.id, null, field, val)}
            />
        </>
    );

    return {
        t,
        items,
        attributesFilter,
        summaryVisible,
        setSummaryVisible,
        handleAddGroup,
        handleDeleteItem,
        handleAddRuleToGroup,
        handleDeleteRuleFromGroup,
        handleAddTopLevelRule,
        handleFieldChange,
        handleConditionChange,
        handleValueChange,
        handleDateChange,
        handleFromDateChange,
        handleToDateChange,
        handleRangeValueChange,
        handleAssignMultiple,
        handleValidityChange,
        handleItemConnectorChange,
        handleRuleConnectorChange,
        handleSearch,
        handleClear,
        summary,
        renderStandaloneRule
    };
};

export default SearchForApp;
